import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  FileText,
  GitBranch,
  BarChart3,
  Settings,
  Download,
  Search,
  File,
  Cog,
  Workflow,
  Monitor
} from 'lucide-react';
import { diffLines } from 'diff';
import { CANON_ENGINE_URL as API_BASE } from '../lib/engineUrls';

// Type definition for diff library change objects
interface Change {
  value: string;
  added?: boolean;
  removed?: boolean;
}

interface CanonStatus {
  sections: number;
  sources: number;
  lastSynthesis: string;
  validationStatus: string;
  dependencyEdges: number;
  circularDeps: number;
  brokenRefs: number;
  avgConfidence: number;
  validationErrors: number;
  warnings: number;
}

interface CanonSection {
  id: string;
  title: string;
  type: string;
  level: string;
  intent: string;
  confidence: number;
  content: string;
  file: string;
  isMain?: boolean;
}

interface ValidationError {
  message: string;
  type: 'error' | 'warning';
  section?: string;
}

const CanonEngine: React.FC = () => {
  const [status, setStatus] = useState<CanonStatus | null>(null);
  const [sections, setSections] = useState<CanonSection[]>([]);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activePage, setActivePage] = useState<'overview' | 'synthesis' | 'validation' | 'staging' | 'dependencies' | 'lab' | 'documentation' | 'architecture'>('overview');
  const [selectedSection, setSelectedSection] = useState<CanonSection | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType] = useState<string>('all');
  const [changelog, setChangelog] = useState<any>(null);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadStatus();
    loadSections();
    loadErrors();
    loadChangelog();
  }, []);

  const loadChangelog = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/changelog`);
      if (response.ok) {
        const data = await response.json();
        setChangelog(data.data);
      }
    } catch (error) {
      console.error('Failed to load changelog:', error);
    }
  };

  const loadStatus = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/status`);
      if (response.ok) {
        const data = await response.json();
        // Transform the response to match expected format
        setStatus({
          sections: 0,
          sources: 0,
          lastSynthesis: data.timestamp,
          validationStatus: 'unknown',
          dependencyEdges: 0,
          circularDeps: 0,
          brokenRefs: 0,
          avgConfidence: 0,
          validationErrors: 0,
          warnings: 0,
          ...data.data
        });
      }
    } catch (error) {
      // Server might not be running - that's okay
      console.warn('Canon Engine server not available:', error);
    }
  };

  const loadSections = async () => {
    try {
      // Sections endpoint doesn't exist yet, so we'll skip for now
      // This would require adding an endpoint to the server
      setSections([]);
    } catch (error) {
      console.warn('Sections endpoint not available:', error);
    }
  };

  const loadErrors = async () => {
    try {
      // Errors endpoint doesn't exist yet, so we'll skip for now
      // This would require adding an endpoint to the server
      setErrors([]);
    } catch (error) {
      console.warn('Errors endpoint not available:', error);
    }
  };

  const runOperation = async (operation: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/canon/${operation}`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        throw new Error(`Operation failed: ${response.statusText}`);
      }

      const result = await response.text();
      console.log(`Operation ${operation} completed:`, result);
      
      // Refresh data
      await loadStatus();
      await loadSections();
      await loadErrors();
    } catch (error) {
      console.error(`Failed to run ${operation}:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  const runStagingOperation = async (operation: string) => {
    setIsLoading(true);
    try {
      let response: Response;
      
      switch (operation) {
        case 'synthesis':
          response = await fetch(`${API_BASE}/api/staging`, { method: 'POST' });
          break;
        case 'promote':
          // Show approval dialog first
          if (changelog?.requires_approval) {
            setShowApprovalDialog(true);
            setIsLoading(false);
            return;
          }
          response = await fetch(`${API_BASE}/api/promote`, { 
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ confirm: true })
          });
          break;
        case 'status':
          response = await fetch(`${API_BASE}/api/staging-status`);
          break;
        default:
          throw new Error(`Unknown staging operation: ${operation}`);
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: { message: response.statusText } }));
        throw new Error(errorData.error?.message || `Staging operation failed: ${response.statusText}`);
      }

      const result = await response.json();
      console.log(`Staging operation ${operation} completed:`, result);
      
      // Refresh data
      await loadStatus();
      await loadSections();
      await loadErrors();
      await loadChangelog();
      setShowApprovalDialog(false);
    } catch (error: any) {
      console.error(`Failed to run staging ${operation}:`, error);
      alert(`Error: ${error.message || 'Operation failed'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprovePromotion = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/promote`, { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: true })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: { message: response.statusText } }));
        throw new Error(errorData.error?.message || 'Promotion failed');
      }

      const result = await response.json();
      console.log('Promotion completed:', result);
      
      // Refresh data
      await loadStatus();
      await loadSections();
      await loadErrors();
      await loadChangelog();
      setShowApprovalDialog(false);
      alert('✅ Staging successfully promoted to production!');
    } catch (error: any) {
      console.error('Failed to promote:', error);
      alert(`Error: ${error.message || 'Promotion failed'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredSections = (sections || []).filter(section => {
    const matchesSearch = section.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         section.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || section.type === filterType;
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status: string) => {
    if (status === 'Passed') return 'text-[var(--green)] dark:text-[var(--text-ter)]';
    if (status === 'Failed') return 'text-[var(--coral)]';
    return 'text-[var(--mustard)]';
  };

  const getStatusIcon = (status: string) => {
    if (status === 'Passed') return <CheckCircle className="w-4 h-4 text-[var(--green)]" />;
    if (status === 'Failed') return <AlertTriangle className="w-4 h-4 text-[var(--coral)]" />;
    return <AlertTriangle className="w-4 h-4 text-[var(--mustard)]" />;
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'framework': return <Settings className="w-4 h-4 text-[var(--text-ter)]" />;
      case 'guide': return <BookOpen className="w-4 h-4 text-[var(--green)]" />;
      case 'architecture': return <GitBranch className="w-4 h-4 text-[var(--text-ter)]" />;
      case 'reference': return <FileText className="w-4 h-4 text-[var(--mustard)]" />;
      default: return <File className="w-4 h-4 text-[var(--text-sec)]" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'framework': return 'bg-[var(--slate-dim)] text-[var(--text-sec)]';
      case 'guide': return 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]';
      case 'architecture': return 'bg-[var(--slate-dim)] text-[var(--text-sec)]';
      case 'reference': return 'bg-[var(--mustard-dim)] text-[var(--mustard)]';
      default: return 'bg-[var(--hover-tint)] text-[var(--text-pri)]';
    }
  };

  const pages = [
    { id: 'overview', label: 'Overview', icon: BarChart3, description: 'System status and health metrics' },
    { id: 'synthesis', label: 'Synthesis', icon: Cog, description: 'Generate and update Canon from sources' },
    { id: 'validation', label: 'Validation', icon: CheckCircle, description: 'Validate Canon structure and dependencies' },
    { id: 'staging', label: 'Staging', icon: Workflow, description: 'Manage staging environment and promotions' },
    { id: 'dependencies', label: 'Dependencies', icon: GitBranch, description: 'View dependency graph and relationships' },
    { id: 'lab', label: 'Lab', icon: Settings, description: 'Experimental features and tools' }
  ];

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Status Cards */}
      {status && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-[var(--bg-raised)] shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-[var(--text-sec)]">Sections</p>
                <p className="text-2xl font-bold text-[var(--text-pri)]">{status.sections}</p>
              </div>
              <FileText className="w-8 h-8 text-[var(--text-ter)]" />
            </div>
          </div>
          
          <div className="bg-[var(--bg-raised)] shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-[var(--text-sec)]">Source Files</p>
                <p className="text-2xl font-bold text-[var(--text-pri)]">{status.sources}</p>
              </div>
              <Settings className="w-8 h-8 text-[var(--green)]" />
            </div>
          </div>
          
          <div className="bg-[var(--bg-raised)] shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-[var(--text-sec)]">Validation Status</p>
                <div className="flex items-center gap-2 mt-1">
                  {getStatusIcon(status.validationStatus)}
                  <span className={`text-sm font-medium ${getStatusColor(status.validationStatus)}`}>
                    {status.validationStatus}
                  </span>
                </div>
              </div>
              <CheckCircle className="w-8 h-8 text-[var(--text-ter)]" />
            </div>
          </div>
          
          <div className="bg-[var(--bg-raised)] shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-[var(--text-sec)]">Errors</p>
                <p className="text-2xl font-bold text-[var(--coral)]">{status.validationErrors}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-[var(--coral)]" />
            </div>
          </div>
        </div>
      )}

      {/* System Health */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[var(--bg-raised)] shadow p-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">System Health</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-[var(--text-sec)]">Dependency Edges</span>
              <span className="font-medium">{status?.dependencyEdges || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--text-sec)]">Circular Dependencies</span>
              <span className="font-medium text-[var(--coral)]">{status?.circularDeps || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--text-sec)]">Broken References</span>
              <span className="font-medium text-[var(--coral)]">{status?.brokenRefs || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--text-sec)]">Average Confidence</span>
              <span className="font-medium">{(status?.avgConfidence || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>
        
        <div className="bg-[var(--bg-raised)] shadow p-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <button
              onClick={() => runOperation('synthesis')}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Run Synthesis
            </button>
            <button
              onClick={() => runOperation('validation')}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[var(--green)] text-white hover:bg-[var(--green)] disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4" />
              Validate Canon
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDocumentation = () => {
    // Organize sections into the taxonomy structure
    const taxonomy = {
      'System Overview': {
        icon: '📘',
        description: 'Executive-level summaries and cross-cutting documentation',
        sections: filteredSections.filter(s => 
          s.title.toLowerCase().includes('canon') && 
          (s.title.toLowerCase().includes('implementation') || 
           s.title.toLowerCase().includes('authoritative') ||
           s.title.toLowerCase().includes('production documentation'))
        )
      },
      'Assessment Framework': {
        icon: '🧠',
        description: 'Assessment logic, structure, and interpretation',
        sections: filteredSections.filter(s => 
          s.title.toLowerCase().includes('assessment')
        )
      },
      'Knowledge Architecture': {
        icon: '🗂️',
        description: 'Information model, taxonomy, and storage',
        sections: filteredSections.filter(s => 
          s.title.toLowerCase().includes('knowledge')
        )
      },
      'Technical Architecture': {
        icon: '⚙️',
        description: 'Technical stack, APIs, integrations',
        sections: filteredSections.filter(s => 
          s.title.toLowerCase().includes('technical') && 
          s.title.toLowerCase().includes('architecture')
        )
      },
      'Development Guide': {
        icon: '🧩',
        description: 'Build practices, conventions, standards',
        sections: filteredSections.filter(s => 
          s.title.toLowerCase().includes('development')
        )
      },
      'Deployment & Operations': {
        icon: '🚀',
        description: 'Deploy, manage, and operate the system',
        sections: filteredSections.filter(s => 
          s.title.toLowerCase().includes('deployment') ||
          s.title.toLowerCase().includes('operations')
        )
      },
      'Technical Documentation': {
        icon: '💻',
        description: 'User guides and reference materials',
        sections: filteredSections.filter(s => 
          s.title.toLowerCase().includes('user guide') ||
          s.title.toLowerCase().includes('port allocation')
        )
      }
  };

  return (
      <div className="space-y-6">
        {/* Search and Filter Bar */}
        <div className="bg-[var(--bg-raised)] shadow p-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--text-ter)] w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search Canon documentation..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Full-width Content Display */}
        {selectedSection ? (
          <div className="bg-[var(--bg-raised)] shadow">
            <div className="p-8 border-b border-[var(--rule)]">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-4xl font-bold text-[var(--text-pri)] flex items-center gap-4">
                    {getTypeIcon(selectedSection.type)}
                    {selectedSection.title}
                  </h1>
                  <div className="flex items-center gap-4 mt-4">
                    <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${getTypeColor(selectedSection.type)}`}>
                      {selectedSection.type}
                    </span>
                    <span className="text-sm text-[var(--text-ter)]">
                      {selectedSection.level} • {selectedSection.intent}
                    </span>
                    <span className="text-sm text-[var(--text-ter)]">
                      Confidence: {(selectedSection.confidence * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedSection(null)}
                  className="text-[var(--text-ter)] hover:text-[var(--text-sec)] text-3xl"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-8">
              <div className="prose prose-xl max-w-none">
                <div className="whitespace-pre-wrap text-[var(--text-pri)] leading-relaxed text-lg">
                  {selectedSection.content}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {Object.entries(taxonomy).map(([category, data]) => (
              <div key={category} className="bg-[var(--bg-raised)] shadow">
                <div className="p-6 border-b border-[var(--rule)]">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{data.icon}</span>
                    <div>
                      <h3 className="text-xl font-semibold text-[var(--text-pri)]">{category}</h3>
                      <p className="text-sm text-[var(--text-sec)]">{data.description}</p>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {data.sections.length > 0 ? (
                      data.sections.map((section) => (
                        <button
                          key={section.id}
                          onClick={() => setSelectedSection(section)}
                          className="w-full text-left p-6 border border-[var(--rule)] hover:border-[var(--slate)] hover:bg-[var(--slate-dim)] transition-all duration-200"
                        >
                          <div className="flex items-start gap-4">
                            {getTypeIcon(section.type)}
                            <div className="flex-1">
                              <h4 className="text-lg font-medium text-[var(--text-pri)] mb-2">
                                {section.title}
                              </h4>
                              <div className="flex items-center gap-3">
                                <span className={`inline-flex items-center px-3 py-1 rounded text-sm font-medium ${getTypeColor(section.type)}`}>
                                  {section.type}
                                </span>
                                {section.isMain && (
                                  <span className="text-sm text-[var(--text-ter)] font-medium">Main</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="text-sm text-[var(--text-ter)] text-center py-8">
                        No documents in this category
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderLab = () => (
    <div className="space-y-6">
      <div className="bg-[var(--bg-raised)] shadow p-6">
        <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Canon Lab</h3>
        <p className="text-[var(--text-sec)] mb-6">
          Experimental features and tools for exploring Canon documentation and system architecture.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Documentation Card */}
          <div className="bg-[var(--surface)] p-6">
            <div className="flex items-center gap-3 mb-4">
              <BookOpen className="w-6 h-6 text-[var(--text-ter)]" />
              <h4 className="text-lg font-semibold text-[var(--text-pri)]">Documentation Browser</h4>
            </div>
            <p className="text-[var(--text-sec)] mb-4">
              Browse and explore Canon documentation with full-width reading experience.
            </p>
            <button
              onClick={() => {
                setActivePage('documentation');
                // Store the current page to return to lab
                sessionStorage.setItem('canonReturnPage', 'lab');
              }}
              className="w-full px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] transition-colors"
            >
              Open Documentation Browser
            </button>
          </div>

          {/* Architecture Card */}
          <div className="bg-[var(--surface)] p-6">
            <div className="flex items-center gap-3 mb-4">
              <Monitor className="w-6 h-6 text-[var(--text-ter)]" />
              <h4 className="text-lg font-semibold text-[var(--text-pri)]">System Architecture</h4>
            </div>
            <p className="text-[var(--text-sec)] mb-4">
              Explore system architecture diagrams and technical specifications.
            </p>
            <button
              onClick={() => {
                setActivePage('architecture');
                // Store the current page to return to lab
                sessionStorage.setItem('canonReturnPage', 'lab');
              }}
              className="w-full px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] transition-colors"
            >
              Open Architecture Viewer
            </button>
          </div>
        </div>
      </div>

      {/* Render Documentation or Architecture based on sub-page */}
      {activePage === 'documentation' && (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setActivePage('lab')}
              className="flex items-center gap-2 px-4 py-2 text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors"
            >
              ← Back to Lab
            </button>
            <h2 className="text-xl font-semibold text-[var(--text-pri)]">Documentation Browser</h2>
          </div>
          {renderDocumentation()}
        </div>
      )}
      
      {activePage === 'architecture' && (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setActivePage('lab')}
              className="flex items-center gap-2 px-4 py-2 text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors"
            >
              ← Back to Lab
            </button>
            <h2 className="text-xl font-semibold text-[var(--text-pri)]">System Architecture</h2>
          </div>
          {renderArchitecture()}
        </div>
      )}
    </div>
  );

  const renderArchitecture = () => (
    <div className="space-y-6">
      <div className="bg-[var(--bg-raised)] shadow p-8">
        <div className="prose prose-lg max-w-none">
          <h3 className="text-xl font-semibold text-[var(--text-pri)] mb-4">Architecture Overview</h3>
          <p className="text-[var(--text-sec)] mb-6">
            The Peak Priorities system architecture consists of multiple interconnected components designed to support 
            assessment frameworks, knowledge management, and decision-making processes.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-[var(--surface)] p-6">
              <h4 className="text-lg font-semibold text-[var(--text-pri)] mb-3">Core Components</h4>
              <ul className="space-y-2 text-[var(--text-sec)]">
                <li>• Canon Engine (Documentation Management)</li>
                <li>• Assessment Framework</li>
                <li>• Knowledge Architecture</li>
                <li>• Registry Demo (Elevation Site)</li>
              </ul>
            </div>
            <div className="bg-[var(--surface)] p-6">
              <h4 className="text-lg font-semibold text-[var(--text-pri)] mb-3">Technical Stack</h4>
              <ul className="space-y-2 text-[var(--text-sec)]">
                <li>• React.js Frontend</li>
                <li>• Node.js Backend</li>
                <li>• Express.js API Server</li>
                <li>• Markdown Documentation</li>
              </ul>
            </div>
          </div>

          <div className="bg-[var(--slate-dim)] p-6">
            <h4 className="text-lg font-semibold text-[var(--text-sec)] mb-3">System Integration</h4>
            <p className="text-[var(--text-sec)]">
              All components are integrated through the Canon Engine, which provides unified documentation management, 
              validation, and staging capabilities across the entire system.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSynthesis = () => (
    <div className="space-y-6">
      <div className="bg-[var(--bg-raised)] shadow p-6">
        <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Canon Synthesis</h3>
        <p className="text-[var(--text-sec)] mb-6">
          Generate and update the Canon from source files. This operation processes all source documents and creates the authoritative Canon.
        </p>
        <div className="space-y-4">
          <button
            onClick={() => runOperation('synthesis')}
            disabled={isLoading}
            className="flex items-center gap-2 px-6 py-3 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            Run Canon Synthesis
          </button>
          <div className="text-sm text-[var(--text-ter)]">
            Last synthesis: {status?.lastSynthesis || 'Never'}
          </div>
        </div>
      </div>
    </div>
  );

  const renderValidation = () => (
    <div className="space-y-6">
      <div className="bg-[var(--bg-raised)] shadow p-6">
        <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Canon Validation</h3>
        <p className="text-[var(--text-sec)] mb-6">
          Validate the Canon structure, dependencies, and consistency. Check for errors and warnings.
        </p>
        <div className="space-y-4">
          <button
            onClick={() => runOperation('validation')}
            disabled={isLoading}
            className="flex items-center gap-2 px-6 py-3 bg-[var(--green)] text-white hover:bg-[var(--green)] disabled:opacity-50"
          >
            <CheckCircle className="w-5 h-5" />
            Run Validation
          </button>
          <div className="text-sm text-[var(--text-ter)]">
            Status: <span className={getStatusColor(status?.validationStatus || '')}>{status?.validationStatus || 'Unknown'}</span>
          </div>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="bg-[var(--bg-raised)] shadow p-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Validation Errors</h3>
          <div className="space-y-2">
            {errors.map((error, index) => (
              <div key={index} className="flex items-start gap-2 p-3 bg-[var(--coral-dim)] border border-[var(--coral)]">
                <AlertTriangle className="w-4 h-4 text-[var(--coral)] mt-0.5" />
                <span className="text-sm text-[var(--coral)]">{error.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderStaging = () => (
    <div className="space-y-6">
      <div className="bg-[var(--bg-raised)] shadow p-6">
        <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Staging Environment</h3>
        <p className="text-[var(--text-sec)] mb-6">
          Manage the staging environment for testing Canon changes before promoting to production.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <button
            onClick={() => runStagingOperation('synthesis')}
            disabled={isLoading}
            className="flex items-center gap-2 px-6 py-3 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50"
          >
            <Workflow className="w-5 h-5" />
            Create Staging
          </button>
          <button
            onClick={() => runStagingOperation('promote')}
            disabled={isLoading || !changelog}
            className="flex items-center gap-2 px-6 py-3 bg-[var(--green)] text-white hover:bg-[var(--green)] disabled:opacity-50"
          >
            <Download className="w-5 h-5" />
            Promote to Production
          </button>
        </div>

        {/* Changelog Display */}
        {changelog && (
          <div className="mt-6 border-t pt-6">
            <h4 className="text-md font-semibold text-[var(--text-pri)] mb-4">Changelog Summary</h4>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
              <div className="bg-[var(--slate-dim)] p-3">
                <div className="text-2xl font-bold text-[var(--text-sec)]">{changelog.summary?.total_new || 0}</div>
                <div className="text-sm text-[var(--text-sec)]">New Sections</div>
              </div>
              <div className="bg-[var(--mustard-dim)] p-3">
                <div className="text-2xl font-bold text-[var(--mustard)]">{changelog.summary?.total_modified || 0}</div>
                <div className="text-sm text-[var(--mustard)]">Modified</div>
              </div>
              <div className="bg-[var(--coral-dim)] p-3">
                <div className="text-2xl font-bold text-[var(--coral)]">{changelog.summary?.total_deleted || 0}</div>
                <div className="text-sm text-[var(--coral)]">Deleted</div>
              </div>
              <div className={` p-3 ${changelog.summary?.total_definition_changes > 0 ? 'bg-[var(--coral-dim)] border-2 border-[var(--coral)]' : 'bg-[var(--slate-dim)]'}`}>
                <div className={`text-2xl font-bold ${changelog.summary?.total_definition_changes > 0 ? 'text-[var(--coral)]' : 'text-[var(--text-ter)]'}`}>
                  {changelog.summary?.total_definition_changes || 0}
                </div>
                <div className={`text-sm ${changelog.summary?.total_definition_changes > 0 ? 'text-[var(--coral)] font-semibold' : 'text-[var(--text-ter)]'}`}>
                  ⚠️ Definition Changes
                </div>
              </div>
              <div className="bg-[var(--slate-dim)] p-3">
                <div className="text-2xl font-bold text-[var(--text-sec)]">{changelog.summary?.total_dependency_changes || 0}</div>
                <div className="text-sm text-[var(--text-sec)]">Dependency Changes</div>
              </div>
            </div>

            {/* Critical Issues - Definition Changes */}
            {changelog.critical_issues && changelog.critical_issues.length > 0 && (
              <div className="bg-[var(--coral-dim)] border-2 border-[var(--coral)] p-4 mb-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-[var(--coral)] flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h5 className="font-bold text-[var(--coral)] mb-2 text-lg">⚠️ CRITICAL: Definition Changes Detected</h5>
                    <p className="text-sm text-[var(--coral)] mb-3">
                      {changelog.summary?.total_definition_changes || 0} definition changes detected. This may indicate namespace collisions or term overloading. Review each change carefully before approving.
                    </p>
                    <div className="mt-3">
                      <button
                        onClick={() => {
                          const defSection = document.getElementById('definition-changes');
                          defSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                        className="text-sm font-semibold text-[var(--coral)] hover:text-[var(--coral)] underline"
                      >
                        View Definition Changes →
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {changelog.requires_approval && (
              <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-4 mb-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h5 className="font-semibold text-[var(--mustard)] mb-2">Approval Required</h5>
                    <ul className="list-disc list-inside space-y-1 text-sm text-[var(--mustard)]">
                      {changelog.approval_required_for?.slice(0, 5).map((reason: string, idx: number) => (
                        <li key={idx}>{reason}</li>
                      ))}
                      {changelog.approval_required_for?.length > 5 && (
                        <li className="text-[var(--mustard)]">... and {changelog.approval_required_for.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Term Usage Issues - Two Separate Types */}
            {changelog.definition_changes && (
              <div id="definition-changes" className="mt-6 border-t pt-6">
                {/* Wrong Usage - Term used incorrectly */}
                {changelog.definition_changes.wrong_usage && changelog.definition_changes.wrong_usage.length > 0 && (
                  <div className="mb-6">
                    <h5 className="font-bold text-[var(--coral)] mb-3 text-lg flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Wrong Usage ({changelog.definition_changes.wrong_usage.length}) - TERM USED INCORRECTLY
                    </h5>
                    <p className="text-sm text-[var(--coral)] mb-4">
                      These terms are being used in a way that contradicts their definitions. Fix the usage to match the definition.
                    </p>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {changelog.definition_changes.wrong_usage.map((issue: any, idx: number) => (
                        <div key={idx} className="bg-[var(--coral-dim)] border-2 border-[var(--coral)] p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="font-bold text-[var(--coral)] text-lg">{issue.term}</span>
                            <span className="px-2 py-1 text-xs rounded-full bg-[var(--coral)] text-white font-bold">
                              WRONG USAGE
                            </span>
                            <span className="text-xs text-[var(--text-sec)]">Section: {issue.section}</span>
                            {issue.lineNumber && (
                              <span className="text-xs text-[var(--text-ter)]">Line: {issue.lineNumber}</span>
                            )}
                            {issue.sectionFile && (
                              <span className="text-xs text-[var(--text-ter)] font-mono">{issue.sectionFile}</span>
                            )}
                          </div>
                          
                          {/* Context with line numbers */}
                          {issue.context && (
                            <div className="mb-3">
                              <div className="font-semibold mb-1 text-xs text-[var(--text-sec)]">Context (with line numbers):</div>
                              <div className="bg-[var(--bg)] text-[var(--text-pri)] rounded p-3 text-xs font-mono max-h-48 overflow-y-auto">
                                {issue.context.split('\n').map((line: string, lineIdx: number) => {
                                  const lineNum = (issue.lineNumber || 0) - 2 + lineIdx;
                                  const isHighlighted = lineIdx === (issue.highlightedLine || 0);
                                  return (
                                    <div 
                                      key={lineIdx} 
                                      className={isHighlighted ? 'bg-[var(--coral)] text-[var(--mustard)] px-2 py-0.5 -mx-2' : ''}
                                    >
                                      <span className="text-[var(--text-ter)] mr-3 w-8 inline-block">{lineNum}</span>
                                      {line}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <div className="font-semibold mb-1 text-xs text-[var(--text-sec)]">Actual Usage:</div>
                              <div className="bg-[var(--bg-raised)] border border-[var(--coral)] rounded p-2 text-xs italic">
                                "{issue.usage}"
                              </div>
                              {issue.beforeContext && (
                                <div className="text-xs text-[var(--text-ter)] mt-1">
                                  Before: ...{issue.beforeContext.substring(Math.max(0, issue.beforeContext.length - 30))}
                                </div>
                              )}
                              {issue.afterContext && (
                                <div className="text-xs text-[var(--text-ter)] mt-1">
                                  After: {issue.afterContext.substring(0, 50)}...
                                </div>
                              )}
                            </div>
                            <div>
                              <div className="font-semibold mb-1 text-xs text-[var(--text-sec)]">Expected (from glossary):</div>
                              <div className="bg-[var(--bg-raised)] border border-[var(--green)] rounded p-2 text-xs">
                                {issue.expected}
                              </div>
                              {issue.sourceDefinition && (
                                <div className="text-xs text-[var(--text-ter)] mt-1">
                                  Source: {issue.sourceDefinition}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-xs text-[var(--coral)] mt-2 font-semibold">
                            Reason: {issue.reason}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Definition Needs Update - Usage suggests definition should change */}
                {changelog.definition_changes.definition_needs_update && changelog.definition_changes.definition_needs_update.length > 0 && (
                  <div className="mb-6">
                    <h5 className="font-bold text-[var(--mustard)] mb-3 text-lg flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Definition Needs Update ({changelog.definition_changes.definition_needs_update.length}) - CONSIDER UPDATING DEFINITION
                    </h5>
                    <p className="text-sm text-[var(--mustard)] mb-4">
                      These terms are being used in a way that suggests the definition might need updating. Review whether the definition should change to match the usage.
                    </p>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {changelog.definition_changes.definition_needs_update.map((issue: any, idx: number) => (
                        <div key={idx} className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="font-bold text-[var(--mustard)] text-lg">{issue.term}</span>
                            <span className="px-2 py-1 text-xs rounded-full bg-[var(--mustard)] text-white font-bold">
                              DEFINITION UPDATE SUGGESTED
                            </span>
                            <span className="text-xs text-[var(--text-sec)]">Section: {issue.section}</span>
                            {issue.lineNumber && (
                              <span className="text-xs text-[var(--text-ter)]">Line: {issue.lineNumber}</span>
                            )}
                            {issue.sectionFile && (
                              <span className="text-xs text-[var(--text-ter)] font-mono">{issue.sectionFile}</span>
                            )}
                          </div>
                          
                          {/* Context with line numbers */}
                          {issue.context && (
                            <div className="mb-3">
                              <div className="font-semibold mb-1 text-xs text-[var(--text-sec)]">Context (with line numbers):</div>
                              <div className="bg-[var(--bg)] text-[var(--text-pri)] rounded p-3 text-xs font-mono max-h-48 overflow-y-auto">
                                {issue.context.split('\n').map((line: string, lineIdx: number) => {
                                  const lineNum = (issue.lineNumber || 0) - 2 + lineIdx;
                                  const isHighlighted = lineIdx === (issue.highlightedLine || 0);
                                  return (
                                    <div 
                                      key={lineIdx} 
                                      className={isHighlighted ? 'bg-[var(--mustard)] text-[var(--mustard)] px-2 py-0.5 -mx-2' : ''}
                                    >
                                      <span className="text-[var(--text-ter)] mr-3 w-8 inline-block">{lineNum}</span>
                                      {line}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <div className="font-semibold mb-1 text-xs text-[var(--text-sec)]">Current Usage:</div>
                              <div className="bg-[var(--bg-raised)] border border-[var(--mustard)] rounded p-2 text-xs italic">
                                "{issue.usage}"
                              </div>
                              {issue.beforeContext && (
                                <div className="text-xs text-[var(--text-ter)] mt-1">
                                  Before: ...{issue.beforeContext.substring(Math.max(0, issue.beforeContext.length - 30))}
                                </div>
                              )}
                              {issue.afterContext && (
                                <div className="text-xs text-[var(--text-ter)] mt-1">
                                  After: {issue.afterContext.substring(0, 50)}...
                                </div>
                              )}
                            </div>
                            <div>
                              <div className="font-semibold mb-1 text-xs text-[var(--text-sec)]">Current Definition (glossary):</div>
                              <div className="bg-[var(--bg-raised)] border border-[var(--coral)] rounded p-2 text-xs">
                                {issue.currentDefinition}
                              </div>
                            </div>
                          </div>
                          
                          <div className="mt-3">
                            <div className="font-semibold mb-1 text-xs text-[var(--text-sec)]">Suggested New Definition:</div>
                            <div className="bg-[var(--bg-raised)] border border-[var(--green)] rounded p-2 text-xs">
                              {issue.suggestedDefinition}
                            </div>
                            <div className="mt-2 flex gap-2">
                              <button
                                onClick={async () => {
                                  try {
                                    const response = await fetch(`${API_BASE}/api/glossary/add`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({
                                        term: issue.term,
                                        definition: issue.suggestedDefinition,
                                        category: 'concept',
                                        examples: []
                                      })
                                    });
                                    const result = await response.json();
                                    if (result.status === 'success') {
                                      alert(`Added "${issue.term}" to glossary`);
                                      loadChangelog(); // Reload to refresh
                                    } else {
                                      alert(`Error: ${result.error?.message || 'Failed to add definition'}`);
                                    }
                                  } catch (error) {
                                    alert(`Error: ${error instanceof Error ? error.message : String(error)}`);
                                  }
                                }}
                                className="text-xs px-3 py-1 bg-[var(--green)] text-white rounded hover:bg-[var(--green)]"
                              >
                                Accept & Add to Glossary
                              </button>
                              <button
                                onClick={async () => {
                                  try {
                                    const response = await fetch(`${API_BASE}/api/glossary/add-alternate`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({
                                        term: issue.term,
                                        definition: issue.suggestedDefinition,
                                        context: issue.usage,
                                        examples: []
                                      })
                                    });
                                    const result = await response.json();
                                    if (result.status === 'success') {
                                      alert(`Added alternate definition for "${issue.term}"`);
                                      loadChangelog(); // Reload to refresh
                                    } else {
                                      alert(`Error: ${result.error?.message || 'Failed to add alternate definition'}`);
                                    }
                                  } catch (error) {
                                    alert(`Error: ${error instanceof Error ? error.message : String(error)}`);
                                  }
                                }}
                                className="text-xs px-3 py-1 bg-[var(--slate)] text-white rounded hover:bg-[var(--slate)]"
                              >
                                Accept as 2nd Definition
                              </button>
                              <button className="text-xs px-3 py-1 bg-[var(--slate)] text-white rounded hover:bg-[var(--surface)]">
                                Dismiss
                              </button>
                            </div>
                          </div>
                          
                          <div className="text-xs text-[var(--mustard)] mt-2 font-semibold">
                            Reason: {issue.updateReason}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Collisions - Same term used differently */}
                {changelog.definition_changes.collisions && changelog.definition_changes.collisions.length > 0 && (
                  <div className="mb-6">
                    <h5 className="font-bold text-[var(--text-ter)] mb-3 text-lg flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Term Usage Collisions ({changelog.definition_changes.collisions.length}) - SAME TERM USED DIFFERENTLY
                    </h5>
                    <p className="text-sm text-[var(--text-ter)] mb-4">
                      These terms are being used with different meanings across multiple sections. Review each usage and decide if they should be unified or if multiple valid definitions should be accepted.
                    </p>
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                      {changelog.definition_changes.collisions.map((collision: any, idx: number) => (
                        <div key={idx} className="bg-[var(--slate-dim)] border-2 border-[var(--slate)] p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="font-bold text-[var(--text-sec)] text-xl">{collision.term}</span>
                            <span className="px-2 py-1 text-xs rounded-full bg-[var(--slate)] text-white font-bold">
                              {collision.differentMeanings} DIFFERENT MEANINGS
                            </span>
                            <span className="text-xs text-[var(--text-sec)]">{collision.totalUsages} total usages</span>
                          </div>
                          
                          {/* Side-by-side comparison of all usage groups */}
                          <div className="space-y-3">
                            {collision.usageGroups.map((group: any[], groupIdx: number) => (
                              <div key={groupIdx} className="bg-[var(--bg-raised)] border border-[var(--slate)] p-3">
                                <div className="font-semibold text-sm text-[var(--text-ter)] mb-2">
                                  Usage Pattern {groupIdx + 1} ({group.length} occurrence{group.length > 1 ? 's' : ''})
                                </div>
                                <div className="grid grid-cols-1 gap-2">
                                  {group.map((usage: any, usageIdx: number) => (
                                    <div key={usageIdx} className="bg-[var(--surface)] border border-[var(--rule)] rounded p-2">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="text-xs text-[var(--text-sec)]">Section: {usage.section}</span>
                                        {usage.lineNumber && (
                                          <span className="text-xs text-[var(--text-ter)]">Line: {usage.lineNumber}</span>
                                        )}
                                        {usage.sectionFile && (
                                          <span className="text-xs text-[var(--text-ter)] font-mono">{usage.sectionFile}</span>
                                        )}
                                      </div>
                                      {usage.context && (
                                        <div className="bg-[var(--bg)] text-[var(--text-pri)] rounded p-2 text-xs font-mono max-h-32 overflow-y-auto mb-2">
                                          {usage.context.split('\n').map((line: string, lineIdx: number) => {
                                            const lineNum = (usage.lineNumber || 0) - 2 + lineIdx;
                                            const isHighlighted = lineIdx === (usage.highlightedLine || 0);
                                            return (
                                              <div 
                                                key={lineIdx} 
                                                className={isHighlighted ? 'bg-[var(--slate)] text-[var(--mustard)] px-2 py-0.5 -mx-2' : ''}
                                              >
                                                <span className="text-[var(--text-ter)] mr-3 w-8 inline-block">{lineNum}</span>
                                                {line}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                      <div className="text-xs italic text-[var(--text-sec)]">
                                        "{usage.usage}"
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                <div className="mt-2 flex gap-2">
                                  <button
                                    onClick={async () => {
                                      try {
                                        // Extract definition from first usage in group
                                        const firstUsage = group[0];
                                        const definition = firstUsage.usage || firstUsage.suggestedDefinition || firstUsage.expected;
                                        
                                        const response = await fetch(`${API_BASE}/api/glossary/add`, {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({
                                            term: collision.term,
                                            definition: definition,
                                            category: 'concept',
                                            examples: []
                                          })
                                        });
                                        const result = await response.json();
                                        if (result.status === 'success') {
                                          alert(`Added "${collision.term}" to glossary`);
                                          loadChangelog();
                                        } else {
                                          alert(`Error: ${result.error?.message || 'Failed to add definition'}`);
                                        }
                                      } catch (error) {
                                        alert(`Error: ${error instanceof Error ? error.message : String(error)}`);
                                      }
                                    }}
                                    className="text-xs px-3 py-1 bg-[var(--green)] text-white rounded hover:bg-[var(--green)]"
                                  >
                                    Accept as Valid Definition
                                  </button>
                                  <button
                                    onClick={async () => {
                                      try {
                                        const firstUsage = group[0];
                                        const definition = firstUsage.usage || firstUsage.suggestedDefinition || firstUsage.expected;
                                        
                                        const response = await fetch(`${API_BASE}/api/glossary/add-alternate`, {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({
                                            term: collision.term,
                                            definition: definition,
                                            context: firstUsage.context || firstUsage.usage,
                                            examples: []
                                          })
                                        });
                                        const result = await response.json();
                                        if (result.status === 'success') {
                                          alert(`Added alternate definition for "${collision.term}"`);
                                          loadChangelog();
                                        } else {
                                          alert(`Error: ${result.error?.message || 'Failed to add alternate definition'}`);
                                        }
                                      } catch (error) {
                                        alert(`Error: ${error instanceof Error ? error.message : String(error)}`);
                                      }
                                    }}
                                    className="text-xs px-3 py-1 bg-[var(--slate)] text-white rounded hover:bg-[var(--slate)]"
                                  >
                                    Accept as 2nd Definition
                                  </button>
                                  <button className="text-xs px-3 py-1 bg-[var(--mustard)] text-white rounded hover:bg-[var(--mustard)]">
                                    Mark for Review
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Legacy definition changes (if still present) */}
                {Array.isArray(changelog.definition_changes) && changelog.definition_changes.length > 0 && (
                  <div className="mb-6">
                    <h5 className="font-bold text-[var(--coral)] mb-3 text-lg flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Definition Changes ({changelog.definition_changes.length}) - CRITICAL REVIEW REQUIRED
                    </h5>
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                      {changelog.definition_changes.slice(0, 20).map((change: any, idx: number) => (
                    <div key={idx} className={` p-4 border-2 ${
                      change.is_collision ? 'bg-[var(--coral-dim)] border-[var(--coral)]' : 'bg-[var(--coral-dim)] border-[var(--coral)]'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-bold text-[var(--coral)] text-lg">{change.term}</span>
                        {change.is_collision && (
                          <span className="px-2 py-1 text-xs rounded-full bg-[var(--coral)] text-white font-bold">
                            ⚠️ NAMESPACE COLLISION
                          </span>
                        )}
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          change.type === 'new' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' :
                          change.type === 'deleted' ? 'bg-[var(--coral-dim)] text-[var(--coral)]' :
                          change.type === 'namespace_collision' ? 'bg-[var(--coral)] text-white' :
                          'bg-[var(--mustard-dim)] text-[var(--mustard)]'
                        }`}>
                          {change.type === 'namespace_collision' ? 'COLLISION' : change.type}
                        </span>
                      </div>
                      
                      {change.type === 'namespace_collision' && (
                        <div className="mt-3">
                          <div className="text-sm font-semibold text-[var(--coral)] mb-2">{change.message}</div>
                          {change.definitions && (
                            <div className="space-y-2">
                              <div className="text-xs font-semibold text-[var(--text-sec)]">Different definitions found in:</div>
                              {change.definitions.map((def: any, defIdx: number) => (
                                <div key={defIdx} className="bg-[var(--bg-raised)] border border-[var(--coral)] rounded p-2">
                                  <div className="text-xs font-semibold text-[var(--coral)] mb-1">Section: {def.section}</div>
                                  <div className="text-xs text-[var(--text-pri)] max-h-24 overflow-y-auto">
                                    {def.definition}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          {change.old_definitions && change.new_definitions && (
                            <div className="grid grid-cols-2 gap-4 mt-3">
                              <div>
                                <div className="text-xs font-semibold text-[var(--text-sec)] mb-2">BEFORE (Production):</div>
                                {change.old_definitions.map((def: any, defIdx: number) => (
                                  <div key={defIdx} className="bg-[var(--bg-raised)] border border-[var(--coral)] rounded p-2 mb-2">
                                    <div className="text-xs font-semibold text-[var(--text-sec)] mb-1">{def.section}</div>
                                    <div className="text-xs text-[var(--text-pri)] max-h-24 overflow-y-auto">
                                      {def.definition}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <div>
                                <div className="text-xs font-semibold text-[var(--text-sec)] mb-2">AFTER (Staging):</div>
                                {change.new_definitions.map((def: any, defIdx: number) => (
                                  <div key={defIdx} className="bg-[var(--bg-raised)] border border-[var(--green)] rounded p-2 mb-2">
                                    <div className="text-xs font-semibold text-[var(--text-sec)] mb-1">{def.section}</div>
                                    <div className="text-xs text-[var(--text-pri)] max-h-24 overflow-y-auto">
                                      {def.definition}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {change.type === 'modified' && !change.is_collision && (
                        <div className="grid grid-cols-2 gap-4 mt-3">
                          <div>
                            <div className="text-xs font-semibold text-[var(--text-sec)] mb-1">BEFORE ({change.old_section}):</div>
                            <div className="bg-[var(--bg-raised)] border border-[var(--coral)] rounded p-2 text-xs text-[var(--text-pri)] max-h-32 overflow-y-auto">
                              {change.old || '(not defined)'}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs font-semibold text-[var(--text-sec)] mb-1">AFTER ({change.new_section}):</div>
                            <div className="bg-[var(--bg-raised)] border border-[var(--green)] rounded p-2 text-xs text-[var(--text-pri)] max-h-32 overflow-y-auto">
                              {change.new || '(removed)'}
                            </div>
                          </div>
                        </div>
                      )}
                      {change.type === 'new' && !change.is_collision && (
                        <div className="mt-2">
                          <div className="text-xs font-semibold text-[var(--text-sec)] mb-1">NEW DEFINITION ({change.section}):</div>
                          <div className="bg-[var(--bg-raised)] border border-[var(--green)] rounded p-2 text-xs text-[var(--text-pri)] max-h-32 overflow-y-auto">
                            {change.definition}
                          </div>
                        </div>
                      )}
                      {change.type === 'deleted' && !change.is_collision && (
                        <div className="mt-2">
                          <div className="text-xs font-semibold text-[var(--text-sec)] mb-1">DELETED FROM ({change.old_section}):</div>
                          <div className="bg-[var(--bg-raised)] border border-[var(--coral)] rounded p-2 text-xs text-[var(--text-pri)] max-h-32 overflow-y-auto line-through">
                            {change.old}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                      {changelog.definition_changes.length > 20 && (
                        <div className="text-sm text-[var(--coral)] text-center py-2 font-semibold">
                          ... and {changelog.definition_changes.length - 20} more definition changes
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Modified Sections with Before/After */}
            {changelog.modified_sections && changelog.modified_sections.length > 0 && (
              <div className="mt-6 border-t pt-6">
                <h5 className="font-semibold text-[var(--text-pri)] mb-3">Modified Sections ({changelog.modified_sections.length})</h5>
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {changelog.modified_sections.slice(0, 10).map((section: any, idx: number) => {
                    const sectionKey = `${section.id}-${idx}`;
                    const isExpanded = expandedSections[sectionKey] || false;
                    const toggleExpanded = () => {
                      setExpandedSections(prev => {
                        const newState = { ...prev };
                        newState[sectionKey] = !prev[sectionKey];
                        return newState;
                      });
                    };
                    return (
                      <div key={sectionKey} className="bg-[var(--surface)] border border-[var(--rule)] p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <div className="font-medium text-[var(--text-pri)]">{section.id}</div>
                            <div className="text-[var(--text-sec)] text-xs mt-1">
                              {section.changes?.frontmatter_changes?.length > 0 && (
                                <span className="mr-2">Frontmatter: {section.changes.frontmatter_changes.length}</span>
                              )}
                              {section.changes?.content_changes && <span className="mr-2">Content changed</span>}
                              {section.changes?.definition_changes?.length > 0 && (
                                <span className="mr-2 text-[var(--coral)] font-semibold">⚠️ Definitions: {section.changes.definition_changes.length}</span>
                              )}
                              {section.changes?.dependency_changes?.length > 0 && (
                                <span>Dependencies: {section.changes.dependency_changes.length}</span>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={toggleExpanded}
                            className="text-sm text-[var(--text-ter)] hover:text-[var(--text-ter)] underline cursor-pointer"
                          >
                            {isExpanded ? '▼ Hide' : '▶ Show'} Diff
                          </button>
                        </div>
                        {isExpanded && section.content_before && section.content_after && (
                          <div className="mt-3">
                            <div className="text-xs font-semibold text-[var(--text-sec)] mb-2 flex items-center gap-4">
                              <span>Diff View (Beyond Compare style):</span>
                              <span className="flex items-center gap-2">
                                <span className="px-2 py-0.5 bg-[var(--coral-dim)] text-[var(--coral)] rounded text-xs border border-[var(--coral)]">Removed</span>
                                <span className="px-2 py-0.5 bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] rounded text-xs border border-[var(--green)]">Added</span>
                                <span className="px-2 py-0.5 bg-[var(--mustard-dim)] text-[var(--mustard)] rounded text-xs border border-[var(--mustard)]">Modified</span>
                              </span>
                            </div>
                            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] rounded overflow-hidden">
                              <div className="grid grid-cols-2 divide-x divide-[var(--rule)] max-h-96 overflow-y-auto">
                                {/* Left side - BEFORE */}
                                <div className="font-mono text-xs">
                                  <div className="bg-[var(--hover-tint)] px-3 py-1 font-semibold text-[var(--text-sec)] sticky top-0 border-b border-[var(--rule)]">
                                    BEFORE
                                  </div>
                                  <div className="text-[var(--text-pri)]">
                                    {(() => {
                                      const diffs = diffLines(section.content_before || '', section.content_after || '');
                                      let lineNum = 1;
                                      const result: React.ReactNode[] = [];
                                      
                                      diffs.forEach((part: Change, idx: number) => {
                                        const lines = part.value.split('\n');
                                        if (lines.length > 0 && lines[lines.length - 1] === '') {
                                          lines.pop();
                                        }
                                        
                                        lines.forEach((line: string, lineIdx: number) => {
                                          const currentLineNum = lineNum++;
                                          const isRemoved = part.removed;
                                          const isUnchanged = !part.removed && !part.added;
                                          
                                          // Check if next part is added (making this a modified line)
                                          const isModified = isRemoved && idx < diffs.length - 1 && diffs[idx + 1]?.added;
                                          
                                          if (isRemoved || isUnchanged) {
                                            result.push(
                                              <div
                                                key={`before-${idx}-${lineIdx}`}
                                                className={`
                                                  ${isRemoved ? 'bg-[var(--coral-dim)] text-[var(--coral)] border-l-4 border-[var(--coral)]' : 'text-[var(--text-sec)]'}
                                                  ${isModified ? 'bg-[var(--mustard-dim)]' : ''}
                                                  px-3 py-0.5 flex items-start
                                                `}
                                              >
                                                <span className="text-[var(--text-ter)] mr-3 w-8 inline-block text-right select-none text-xs">
                                                  {currentLineNum}
                                                </span>
                                                <span className="flex-1 whitespace-pre-wrap">
                                                  {line || <span className="text-[var(--text-ter)]">␣</span>}
                                                </span>
                                              </div>
                                            );
                                          } else if (part.added) {
                                            // Show blank line for added lines on left side
                                            result.push(
                                              <div
                                                key={`before-empty-${idx}-${lineIdx}`}
                                                className="px-3 py-0.5 flex items-start bg-[var(--surface)]"
                                              >
                                                <span className="text-[var(--text-ter)] mr-3 w-8 inline-block text-right text-xs">-</span>
                                                <span className="flex-1 text-[var(--text-ter)]">␣</span>
                                              </div>
                                            );
                                          }
                                        });
                                      });
                                      
                                      return result;
                                    })()}
                                  </div>
                                </div>
                                
                                {/* Right side - AFTER */}
                                <div className="font-mono text-xs">
                                  <div className="bg-[var(--hover-tint)] px-3 py-1 font-semibold text-[var(--text-sec)] sticky top-0 border-b border-[var(--rule)]">
                                    AFTER
                                  </div>
                                  <div className="text-[var(--text-pri)]">
                                    {(() => {
                                      const diffs = diffLines(section.content_before || '', section.content_after || '');
                                      let lineNum = 1;
                                      const result: React.ReactNode[] = [];
                                      
                                      diffs.forEach((part: Change, idx: number) => {
                                        const lines = part.value.split('\n');
                                        if (lines.length > 0 && lines[lines.length - 1] === '') {
                                          lines.pop();
                                        }
                                        
                                        lines.forEach((line: string, lineIdx: number) => {
                                          const currentLineNum = lineNum++;
                                          const isAdded = part.added;
                                          const isUnchanged = !part.removed && !part.added;
                                          
                                          // Check if previous part was removed (making this a modified line)
                                          const isModified = isAdded && idx > 0 && diffs[idx - 1]?.removed;
                                          
                                          if (isAdded || isUnchanged) {
                                            result.push(
                                              <div
                                                key={`after-${idx}-${lineIdx}`}
                                                className={`
                                                  ${isAdded ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-l-4 border-[var(--green)]' : 'text-[var(--text-sec)]'}
                                                  ${isModified ? 'bg-[var(--mustard-dim)]' : ''}
                                                  px-3 py-0.5 flex items-start
                                                `}
                                              >
                                                <span className="text-[var(--text-ter)] mr-3 w-8 inline-block text-right select-none text-xs">
                                                  {currentLineNum}
                                                </span>
                                                <span className="flex-1 whitespace-pre-wrap">
                                                  {line || <span className="text-[var(--text-ter)]">␣</span>}
                                                </span>
                                              </div>
                                            );
                                          } else if (part.removed) {
                                            // Show blank line for removed lines on right side
                                            result.push(
                                              <div
                                                key={`after-empty-${idx}-${lineIdx}`}
                                                className="px-3 py-0.5 flex items-start bg-[var(--surface)]"
                                              >
                                                <span className="text-[var(--text-ter)] mr-3 w-8 inline-block text-right text-xs">-</span>
                                                <span className="flex-1 text-[var(--text-ter)]">␣</span>
                                              </div>
                                            );
                                          }
                                        });
                                      });
                                      
                                      return result;
                                    })()}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {changelog.modified_sections.length > 10 && (
                    <div className="text-sm text-[var(--text-ter)] text-center py-2">
                      ... and {changelog.modified_sections.length - 10} more sections
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {!changelog && (
          <div className="mt-6 border-t pt-6">
            <p className="text-[var(--text-ter)] text-sm">No changelog available. Run staging synthesis to generate changelog.</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderDependencies = () => (
    <div className="space-y-6">
      <div className="bg-[var(--bg-raised)] shadow p-6">
        <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Dependency Graph</h3>
        <p className="text-[var(--text-sec)] mb-6">
          View the dependency relationships between Canon sections.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-[var(--surface)] p-4">
            <div className="text-2xl font-bold text-[var(--text-pri)]">{status?.dependencyEdges || 0}</div>
            <div className="text-sm text-[var(--text-sec)]">Dependency Edges</div>
          </div>
          <div className="bg-[var(--surface)] p-4">
            <div className="text-2xl font-bold text-[var(--coral)]">{status?.circularDeps || 0}</div>
            <div className="text-sm text-[var(--text-sec)]">Circular Dependencies</div>
          </div>
          <div className="bg-[var(--surface)] p-4">
            <div className="text-2xl font-bold text-[var(--coral)]">{status?.brokenRefs || 0}</div>
            <div className="text-sm text-[var(--text-sec)]">Broken References</div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--surface)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-[var(--text-pri)] flex items-center gap-3">
                <Monitor className="h-8 w-8 text-[var(--text-ter)]" />
                Canon Engine
              </h1>
              <p className="text-[var(--text-sec)] mt-2">
                Manage and operate your Canon documentation system
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/elevation/canon-engine/guide"
                className="flex items-center gap-2 px-4 py-2 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
              >
                <BookOpen className="h-4 w-4" />
                User Guide
              </Link>
              <button
                onClick={() => {
                  loadStatus();
                  loadSections();
                  loadErrors();
                }}
                className="flex items-center gap-2 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="bg-[var(--bg-raised)] shadow mb-8">
          <div className="border-b border-[var(--rule)]">
            <nav className="flex space-x-8 px-6">
              {pages.map((page) => (
                <button
                  key={page.id}
                  onClick={() => setActivePage(page.id as any)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
                    activePage === page.id
                      ? 'border-[var(--slate)] text-[var(--text-ter)]'
                      : 'border-transparent text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:border-[var(--rule)]'
                  }`}
                >
                  <page.icon className="w-4 h-4" />
                  {page.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Page Content */}
        <div className="bg-[var(--bg-raised)] shadow">
          <div className="p-6">
            {activePage === 'overview' && renderOverview()}
            {activePage === 'synthesis' && renderSynthesis()}
            {activePage === 'validation' && renderValidation()}
            {activePage === 'staging' && renderStaging()}
            {activePage === 'dependencies' && renderDependencies()}
            {activePage === 'lab' && renderLab()}
          </div>
        </div>

        {/* Approval Dialog */}
        {showApprovalDialog && changelog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-[var(--bg-raised)] shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="w-6 h-6 text-[var(--mustard)]" />
                  <h3 className="text-xl font-bold text-[var(--text-pri)]">Approve Promotion to Production</h3>
                </div>
                
                <div className="mb-6">
                  <p className="text-[var(--text-sec)] mb-4">
                    You are about to promote the staging Canon to production. Please review the changes below before confirming.
                  </p>
                  <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-4 mb-4">
                    <h4 className="font-semibold text-[var(--mustard)] mb-2">Summary</h4>
                    <ul className="list-disc list-inside space-y-1 text-sm text-[var(--mustard)]">
                      <li>New sections: {changelog.summary?.total_new || 0}</li>
                      <li>Modified sections: {changelog.summary?.total_modified || 0}</li>
                      <li>Deleted sections: {changelog.summary?.total_deleted || 0}</li>
                      <li>Definition changes: {changelog.summary?.total_definition_changes || 0}</li>
                      <li>Dependency changes: {changelog.summary?.total_dependency_changes || 0}</li>
                    </ul>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleApprovePromotion}
                    disabled={isLoading}
                    className="flex-1 px-4 py-2 bg-[var(--green)] text-white hover:bg-[var(--green)] font-semibold disabled:opacity-50"
                  >
                    Confirm Promotion
                  </button>
                  <button
                    onClick={() => setShowApprovalDialog(false)}
                    className="flex-1 px-4 py-2 bg-[var(--rule-bright)] text-[var(--text-pri)] hover:bg-[var(--surface)] font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CanonEngine;